import React from 'react';
import { Link, LinkProps } from 'react-router-dom';

type BaseProps = {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
};

type ButtonAsButton = BaseProps & React.ButtonHTMLAttributes<HTMLButtonElement> & { to?: undefined };
type ButtonAsLink = BaseProps & LinkProps & { to: string };
type ButtonAsExternalLink = BaseProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; target?: string; rel?: string };

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsExternalLink;

const Button: React.FC<ButtonProps> = ({ children, className = '', variant = 'primary', ...props }) => {
  const baseClasses = 'inline-block text-center px-6 py-2 rounded-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-100';
  
  const variantClasses = {
    primary: 'bg-emerald-500 text-white hover:bg-emerald-600 focus:ring-emerald-500 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40',
    secondary: 'bg-slate-200 text-slate-700 hover:bg-slate-300 focus:ring-slate-400',
  };

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${className}`;

  if (props.to) {
    const { to, target, rel, ...rest } = props as ButtonAsLink | ButtonAsExternalLink;
    
    // If it's an external link (starts with http/https), use anchor tag
    if (to.startsWith('http://') || to.startsWith('https://')) {
      return (
        <a href={to} className={combinedClasses} target={target} rel={rel} {...rest}>
          {children}
        </a>
      );
    }
    
    // Otherwise use React Router Link
    return (
      <Link to={to} className={combinedClasses} {...rest}>
        {children}
      </Link>
    );
  }

  return (
    <button className={combinedClasses} {...(props as ButtonAsButton)}>
      {children}
    </button>
  );
};

export default Button;
